import { useQuery } from '@tanstack/react-query';
import { EmptyState, ListGroup, ListRow, SearchField, Skeleton } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { AmountStatus, FilterChips, Pager, providerName, SERVER_STATUS, useDebounced } from './common.tsx';

type ServerFilter = 'all' | 'active' | 'creating' | 'suspended' | 'failed' | 'deleted';

const FILTERS: { value: ServerFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'active', label: 'Работают' },
  { value: 'creating', label: 'Создаются' },
  { value: 'suspended', label: 'Приостановлены' },
  { value: 'failed', label: 'Ошибки' },
  { value: 'deleted', label: 'Удалены' },
];

const fullName = (user: { firstName: string; lastName: string | null }) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ');

export function AdminServersScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const { usd, date, dateTime } = usePrefs();
  const [status, setStatus] = useState<ServerFilter>('all');
  const [provider, setProvider] = useState('all');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const query = useDebounced(search.trim());
  const servers = useQuery({
    ...trpc.admin.servers.queryOptions({
      status: status === 'all' ? undefined : status,
      provider: provider === 'all' ? undefined : provider,
      search: query,
      offset,
    }),
    placeholderData: (prev) => prev,
  });
  const providerFilters = [
    { value: 'all', label: 'Все поставщики' },
    ...(servers.data?.providers ?? []).map((value) => ({ value, label: providerName(value) })),
  ];

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Серверы{servers.data ? ` · ${servers.data.total}` : ''}</h1>
      <SearchField
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setOffset(0);
        }}
        placeholder="Сервер, IP, пользователь или Telegram ID"
      />
      <FilterChips
        options={FILTERS}
        value={status}
        onChange={(value) => {
          setStatus(value);
          setOffset(0);
        }}
      />
      <FilterChips
        options={providerFilters}
        value={provider}
        onChange={(value) => {
          setProvider(value);
          setOffset(0);
        }}
      />

      {servers.isPending ? (
        <Skeleton height={320} />
      ) : servers.isError ? (
        <div className="ul-error" role="alert">
          {errorText(servers.error)}
        </div>
      ) : servers.data.rows.length === 0 ? (
        <EmptyState icon="server" title="Серверов нет" text="С таким статусом ничего не найдено" />
      ) : (
        <div className={servers.isPlaceholderData ? 'ul-results ul-results--stale' : 'ul-results'}>
          <div className="ul-multiline">
            <ListGroup header={`Найдено ${servers.data.total}`}>
              {servers.data.rows.map((server) => (
                <ListRow
                  key={server.id}
                  icon="server"
                  tone={server.status === 'failed' ? 'crit' : server.status === 'suspended' ? 'warn' : undefined}
                  title={server.name}
                  subtitle={[
                    `${fullName(server.user)}${server.user.username ? ` · @${server.user.username}` : ''}`,
                    `${providerName(server.provider)} · ${server.location}${server.ipv4 ? ` · ${server.ipv4}` : ''}`,
                    server.paidUntil ? `Оплачен до ${date(server.paidUntil)}` : `Создан ${dateTime(server.createdAt)}`,
                    server.powerState ? `Состояние у провайдера: ${server.powerState}` : null,
                  ]
                    .filter(Boolean)
                    .join('\n')}
                  trailing={
                    <AmountStatus amount={`${usd(server.priceCents)}/мес`} map={SERVER_STATUS} status={server.status} />
                  }
                  chevron
                  onClick={() => nav.push({ name: 'adminUser', id: server.user.id })}
                />
              ))}
            </ListGroup>
          </div>
          <Pager offset={offset} total={servers.data.total} onChange={setOffset} />
        </div>
      )}
    </main>
  );
}
